// dashboard_charts_controller.js
import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["activityChart", "completionRing"];
  
  connect() {
    console.log("Dashboard Charts Controller Connected");
    this.initializeCharts();
  }
  
  disconnect() {
    // Destroy charts on disconnect
    if (this.charts) {
      this.charts.forEach(chart => chart.destroy());
    }
  }
  
  initializeCharts() {
    this.charts = [];
    
    // Create activity trend chart if canvas exists
    if (this.hasActivityChartTarget) {
      this.createActivityTrendChart();
    }
    
    // Create completion rings
    this.completionRingTargets.forEach((canvas, index) => {
      this.createCompletionRing(canvas, index);
    });
  }
  
  createActivityTrendChart() {
    const ctx = this.activityChartTarget.getContext('2d');
    
    // Get values from data attributes on the controller element (Stimulus 1.x syntax)
    const ratings7d = parseInt(this.element.dataset.dashboardChartsRatings7d || 0);
    const skillRatings7d = parseInt(this.element.dataset.dashboardChartsSkillRatings7d || 0);
    const fieldEncounters7d = parseInt(this.element.dataset.dashboardChartsFieldEncounters7d || 0);
    
    console.log('Chart data values:', { ratings7d, skillRatings7d, fieldEncounters7d });
    
    // Generate sample data for last 7 days
    const labels = this.getLast7DaysLabels();
    const ratingsData = this.generateTrendData(ratings7d);
    const skillRatingsData = this.generateTrendData(skillRatings7d);
    const fieldEncountersData = this.generateTrendData(fieldEncounters7d);
    
    const chart = new window.Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Ratings',
          data: ratingsData,
          borderColor: '#2563EB',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }, {
          label: 'Skill Ratings',
          data: skillRatingsData,
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }, {
          label: 'Field Encounters',
          data: fieldEncountersData,
          borderColor: '#F59E0B',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          borderWidth: 2,
          tension: 0.4,
          fill: true
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            boxWidth: 12,
            fontSize: 12,
            padding: 15
          }
        },
        scales: {
          yAxes: [{
            ticks: {
              beginAtZero: true,
              fontSize: 11,
              stepSize: 1,
              suggestedMax: 5  // Ensure chart shows at least 0-5 range for small numbers
            },
            gridLines: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          }],
          xAxes: [{
            ticks: {
              fontSize: 11
            },
            gridLines: {
              display: false
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          cornerRadius: 8
        }
      }
    });
    
    this.charts.push(chart);
  }
  
  createCompletionRing(canvas, index) {
    const ctx = canvas.getContext('2d');
    const percentage = parseInt(canvas.dataset.percentage || 0);
    const label = canvas.dataset.label || '';
    
    const chart = new window.Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: ['Completed', 'Remaining'],
        datasets: [{
          data: [percentage, 100 - percentage],
          backgroundColor: [
            index === 0 ? '#2563EB' : index === 1 ? '#10B981' : '#F59E0B',
            '#E5E7EB'
          ],
          borderWidth: 0
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutoutPercentage: 75,
        legend: {
          display: false
        },
        tooltips: {
          enabled: true
        }
      }
    });
    
    this.charts.push(chart);
  }
  
  getLast7DaysLabels() {
    const labels = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      labels.push(date.toLocaleDateString('en-US', { weekday: 'short' }));
    }
    return labels;
  }
  
  generateTrendData(total) {
    // For small numbers (< 10), concentrate activity on random days instead of distributing
    if (total < 10) {
      const data = [0, 0, 0, 0, 0, 0, 0];
      let remaining = total;
      
      // Place activities on random days
      while (remaining > 0) {
        const dayIndex = Math.floor(Math.random() * 7);
        const amount = Math.min(remaining, Math.ceil(Math.random() * 2));
        data[dayIndex] += amount;
        remaining -= amount;
      }
      
      console.log('Generated small number trend data:', data);
      return data;
    }
    
    // For larger numbers, distribute across 7 days with some variance
    const data = [];
    const avg = total / 7;
    for (let i = 0; i < 7; i++) {
      const variance = (Math.random() - 0.5) * avg * 0.4;
      data.push(Math.max(0, Math.round(avg + variance)));
    }
    return data;
  }
}

